import { QRCodeCanvas } from "qrcode.react"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useApi } from "../api"
import { AppHeader } from "../components/AppHeader"
import { HeaderProfile } from "../components/HeaderProfile"
import { Loader } from "../components/Loader"
import { Numeral } from "../components/Numeral"
import { ICargo, ICoin, IProfile, IReferral, IWallet } from "../interfaces"

export const WalletPage = () => {
  const { t } = useTranslation()
  const api = useApi('SAI')

  const [isMounted, setMounted] = useState(false)
  const [profile, setProfile] = useState<IProfile>()
  const [referral, setReferral] = useState<IReferral>()
  const [usdtWallet, setUsdtWallet] = useState<IWallet>()
  const [ethWallet, setEthWallet] = useState<IWallet>()
  const [coinInfo, setCoinInfo] = useState<ICoin>()
  const [ethTransactions, setEthTransactions] = useState([])
  const [ethWalletAddress, setEthWallerAddress] = useState('')

  const fetchProfile = useCallback(() => {
    api.getProfile((cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        setProfile(cargo.info)
      }
    });
  }, [api])

  const fetchWallet = useCallback(() => {
    api.getWallet('USDT', (cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        if (cargo.info.length > 0) {
          setUsdtWallet(cargo.info[0])
        }
      }
    });
    api.getWallet('ETH', (cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        if (cargo.info.length > 0) {
          setEthWallet(cargo.info[0])
          setEthWallerAddress(`0x${cargo.info[0].address.replace('0x','')}`)
        }
      }
    });
  }, [api])

  const fetchCoinInfo = useCallback(() => {
    api.getCoinInfo((cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        setCoinInfo(cargo.info)
      }
    });
  }, [api])

  const fetchReferral = useCallback(() => {
    api.getReferralCode((cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        setReferral(cargo.info)
      }
    });
  }, [api])

  const fetchEthTransactions = useCallback(() => {
    api.getEthTransactions((cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        setEthTransactions(cargo.info)
      }
    });
  }, [api])

  useEffect(() => {
    if (!isMounted) {
      fetchProfile()
      fetchWallet()
      fetchCoinInfo()
      fetchReferral()
      fetchEthTransactions()
    }
    setMounted(true)
  }, [fetchCoinInfo, fetchProfile, fetchReferral, fetchWallet, fetchEthTransactions, isMounted])

  return (
    <>
      <div className="content">
        <div className="colorbk">
          <AppHeader title={t('wallet')}></AppHeader>
          <HeaderProfile
            avator={profile?.avator}
            nickname={profile?.nickname}
            simpleCode={referral?.simpleCode}
          ></HeaderProfile>
          <div className="radius1 cardd"></div>
        </div>
        <div className="bg2" style={{ marginTop: '-20px' }}>
          <div className="cardd2 px-sm-5 py-sm-4 p-3">
            <div className="d-flex justify-content-around text-left">
              <div>
                <div className="text-xs">{t('usdt')} TRC-20</div>
                <h4 className="font-weight-bold">
                  {!usdtWallet ? <Loader /> : <Numeral value={usdtWallet.balance} />}
                </h4>
              </div>
              <div>
                <div className="text-xs">{t('gas')}</div>
                <h4 className="font-weight-bold">
                  {!coinInfo ? <Loader /> : <Numeral value={coinInfo.gas} />}
                </h4>
              </div>
              <div>
                <div className="text-xs">{t('nnego')}</div>
                <h4 className="font-weight-bold">
                  {!coinInfo ? <Loader /> : <Numeral value={coinInfo.nonNegotiable} />}
                </h4>
              </div>
            </div>
            <div className="text-center text-xs pt-3 text-gray-500 text-ellipsis overflow-hidden">
              {!usdtWallet ? '' : <div>{t('usdtAddress')}<br/>{usdtWallet.address}</div>}
            </div>
            <div>
              <div className="d-flex justify-content-between pt-3 mx-[-10px]">
                <a href="/list_deal.html" className="bt-wallet btc">
                  <span className="material-icons-outlined material-i">list_alt</span> {t('trxHistory')}</a>
                <a href="/wallet.html#tran" className="bt-wallet btm">
                  <span className="material-icons-outlined material-i">change_circle</span> {t('transfer')}</a>
                <a href="/push_back.html" className="bt-wallet bty">
                  <span className="material-icons-outlined material-i">campaign</span> {t('referral')}</a>
              </div>
              <div className="d-flex justify-content-between pt-3 mx-[-10px] text-sm">
                <a href="/qrcodeadd.html" className="bt-wallet btr">
                  <span className="material-icons-outlined material-i">control_point_duplicate</span> {t('topUp')}</a>
                <a href="/wallet.html#atm" className="bt-wallet btg">
                  <span className="material-icons-outlined material-i">local_atm</span> {t('withdraw')}</a>
                <a href="/wallet.html#change" className="bt-wallet btb">
                  <span className="material-icons-outlined material-i">published_with_changes</span> {t('convert')}</a>
              </div>
            </div>
          </div>
        </div >
        <div className="bg2">
          <div className="cardd2 px-sm-5 py-sm-4 p-3">
            <div className="d-flex justify-between text-left">
              <div>
                <div className="w-10"><img src="https://ai.supercoin.network/img/coin/ETH.png" alt="ETH logo" /></div>
                <div className="text-xs mt-3">{t('eth')}</div>
                <h4 className="font-weight-bold">
                  {!ethWallet ? <Loader /> : <Numeral value={ethWallet.balance} />}
                </h4>
              </div>
              <div>
                <div>
                {ethWallet && <QRCodeCanvas
                  value={`0x${ethWallet.address.replace('0x', '')}`}
                  fgColor="#495057"
                />}
                </div>
              </div>
            </div>
            <div className="text-left text-sm">{t('ethAddress')}</div>
            <div className="text-center text-xs text-gray-500 text-ellipsis overflow-hidden">
              {!ethWallet ? '' : <div>{`0x${ethWallet.address.replace('0x', '')}`}</div>}
            </div>
            <hr />
            {
              ethTransactions && ethTransactions.map((trx: any, index) => {
                let sign = '';
                if (trx['from'] === ethWalletAddress) {
                  sign = '-'
                } else if (trx['to'] === ethWalletAddress){
                  sign = '+'
                }
                return (
                  <div key={index}>
                    <div className="text-xs d-flex justify-between">
                      <div>{`${new Intl.DateTimeFormat('ko-KR').format(new Date(trx['timeStamp'] * 1000))}`}</div>
                      <div className="text-right">{sign} <Numeral value={trx['value'] * 0.000000000000000001} /></div>
                    </div>
                  </div>
                )
              })
            }
            <hr />
            <div className="text-sm"><a href={`https://etherscan.io/address/${ethWalletAddress}`}>More...</a></div>
          </div>
        </div>
        <div className="bg2">
          <ul className="nav nav-pills my-3">
            <li className="nav-item w-100">
              <a className="btn btn-warning w-100" href="/wallet.html">更多錢包功能</a>
            </li>
          </ul>
        </div>
        <div className="m-86"></div>
      </div>
    </>
  )
}